/**
 * useNarrativeEngine — Central state management and LLM orchestration hook
 * for the Narrative Engine feature. Manages blueprint generation, arc/tone
 * selection, character generation/editing, and prompt block serialization.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import type {
  NarrativeBlueprint,
  NarrativeArc,
  NarrativeTone,
  CharacterBlueprint,
  NarrativeLLMResponse,
} from '@/types/narrative.types';
import { EMPTY_CHARACTER } from '@/types/narrative.types';
import { NARRATIVE_ARCS, getNarrativeArcById } from '@/data/narrativeArcs';
import { NARRATIVE_TONES, getNarrativeToneByGenre, getNarrativeToneById } from '@/data/narrativeTones';
import { generateNarrativeBlueprint, generateSingleField } from '@/services/narrativeService';
import type { SongSeed } from '@/components/RiffWriter/types';
import { getTasteProfileBlockSync } from '@hooks/useTasteProfile';

// ============================================
// Standalone exported function
// ============================================

/**
 * Serialize a NarrativeBlueprint into a prompt text block.
 * Exported standalone so App.tsx / prompt assembly can call it without the hook.
 */
export function buildNarrativeBlock(bp: NarrativeBlueprint): string {
  // Studio Roulette injects an arc + tone with no character sheet; skip the empty character lines.
  const hasCharacter = Object.values(bp.character).some(v => typeof v === 'string' && v.trim() !== '');
  const characterBlock = hasCharacter
    ? `[Character: ${bp.character.stance}]
${bp.character.ageEra}. ${bp.character.backstoryHook} Core wound: ${bp.character.emotionalWound}. ${bp.character.desire}. ${bp.character.obstacle}. ${bp.character.voiceTexture}. ${bp.character.signaturePhrase}. ${bp.character.speaksLike}.

`
    : '';
  return `{NARRATIVE-ENGINE}
${bp.arc.promptModifier}

${characterBlock}${bp.tone.promptModifier}
{/NARRATIVE-ENGINE}`;
}

// ============================================
// Prompt Templates
// ============================================

function buildBlueprintPrompt(seed: SongSeed, tone: NarrativeTone): string {
  return `You are a songwriter's narrative architect. Given a song seed and genre, build a narrator character and select the best emotional arc.

SONG SEED:
Title: ${seed.title}
Concept: ${seed.concept}
Genre: ${seed.genre}
POV/Setting: ${seed.povSetting}
Themes: ${seed.mustHaves.join(', ')}
Avoid: ${seed.avoid.join(', ')}

GENRE STORYTELLING MODE:
${tone.label}: ${tone.storytellingDna}
Conventions: ${tone.lyricConventions.join('; ')}

AVAILABLE ARCS:
${NARRATIVE_ARCS.map(a => `- ${a.id}: "${a.label}" — ${a.shape} (affinity: ${a.genreAffinity.join(', ')})`).join('\n')}

Select the arc that best fits this seed and genre. Then create a vivid, genre-native narrator character.

Respond in JSON:
{
  "selectedArcId": "id of the best arc",
  "arcReasoning": "one sentence why this arc fits",
  "character": {
    "stance": "their role in the story (5-10 words)",
    "ageEra": "age range and cultural/temporal context",
    "backstoryHook": "one sentence of history that haunts them",
    "emotionalWound": "the core damage driving the song",
    "desire": "what they want by the end of the song",
    "obstacle": "what's in the way",
    "voiceTexture": "how they talk — cadence, length, affect",
    "signaturePhrase": "a verbal tic, metaphor pattern, or recurring image they use",
    "speaksLike": "vocabulary world and communication style"
  }
}

Rules:
- The character must feel native to the genre
- Backstory should be specific, not generic
- Voice texture and speaks-like should give a lyricist enough to write dialogue
- Signature phrase should be concrete enough to appear in a chorus
- Avoid cliches: no "broken hearts", no "open roads" unless subverted`;
}

function buildRegenerateCharacterPrompt(bp: NarrativeBlueprint): string {
  return `You are a songwriter's narrative architect. Given the following song context, create a NEW narrator character — different from the previous one but equally vivid and genre-native.

SONG SEED:
Title: ${bp.seedTitle}
Concept: ${bp.seedConcept}
Genre: ${bp.genre}

CURRENT ARC: ${bp.arc.label} — ${bp.arc.shape}
${bp.arc.promptModifier}

CURRENT TONE: ${bp.tone.label}
${bp.tone.promptModifier}

Create a completely different narrator character than before. New backstory, new wound, new voice.

Respond in JSON:
{
  "selectedArcId": "${bp.arc.id}",
  "arcReasoning": "keeping current arc",
  "character": {
    "stance": "their role in the story (5-10 words)",
    "ageEra": "age range and cultural/temporal context",
    "backstoryHook": "one sentence of history that haunts them",
    "emotionalWound": "the core damage driving the song",
    "desire": "what they want by the end of the song",
    "obstacle": "what's in the way",
    "voiceTexture": "how they talk — cadence, length, affect",
    "signaturePhrase": "a verbal tic, metaphor pattern, or recurring image they use",
    "speaksLike": "vocabulary world and communication style"
  }
}

Rules:
- The character must feel native to the genre
- Backstory should be specific, not generic
- Voice texture and speaks-like should give a lyricist enough to write dialogue
- Signature phrase should be concrete enough to appear in a chorus
- Avoid cliches: no "broken hearts", no "open roads" unless subverted
- Make this character DIFFERENT from anything conventional`;
}

function buildRegenerateFieldPrompt(
  bp: NarrativeBlueprint,
  field: keyof CharacterBlueprint
): string {
  const CHARACTER_FIELD_LABELS: Record<keyof CharacterBlueprint, string> = {
    stance: 'Stance (their role in the story)',
    ageEra: 'Age / Era (age range and cultural/temporal context)',
    backstoryHook: 'Backstory Hook (one sentence of history that haunts them)',
    emotionalWound: 'Emotional Wound (the core damage driving the song)',
    desire: 'Desire (what they want by the end of the song)',
    obstacle: 'Obstacle (what\'s in the way)',
    voiceTexture: 'Voice Texture (how they talk — cadence, length, affect)',
    signaturePhrase: 'Signature Phrase (a verbal tic, metaphor pattern, or recurring image)',
    speaksLike: 'Speaks Like (vocabulary world and communication style)',
  };

  const contextLines = Object.entries(bp.character)
    .filter(([key]) => key !== field)
    .map(([key, value]) => `- ${CHARACTER_FIELD_LABELS[key as keyof CharacterBlueprint]}: ${value}`)
    .join('\n');

  return `You are a songwriter's narrative architect. Given the following song context and existing character fields, generate ONLY a new value for the "${CHARACTER_FIELD_LABELS[field]}" field.

SONG SEED:
Title: ${bp.seedTitle}
Concept: ${bp.seedConcept}
Genre: ${bp.genre}

ARC: ${bp.arc.label} — ${bp.arc.shape}
TONE: ${bp.tone.label}

EXISTING CHARACTER FIELDS:
${contextLines}

Generate a fresh, vivid replacement for the "${field}" field that fits the existing character context. Respond with ONLY the field value — no JSON, no labels, no quotes. Just the raw text value.`;
}

// ============================================
// Hook
// ============================================

const STORAGE_KEY = 'hh_narrative_blueprint_draft';

/** Hydrate the in-progress blueprint from localStorage on mount. Returns null if absent or unparseable. */
function loadDraftFromStorage(): NarrativeBlueprint | null {
  try {
    const raw = typeof window === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NarrativeBlueprint;
  } catch {
    return null;
  }
}

function persistDraftToStorage(bp: NarrativeBlueprint | null): void {
  try {
    if (!bp) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(bp));
  } catch {
    // localStorage may be full / disabled — non-fatal
  }
}

export function useNarrativeEngine() {
  // Hydrate from localStorage so a reload mid-narrative doesn't nuke the work.
  // Once the blueprint gets injected to Studio / Studio Team, callers should
  // clear via clearDraft().
  const [blueprint, setBlueprint] = useState<NarrativeBlueprint | null>(loadDraftFromStorage);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist on every blueprint change
  useEffect(() => {
    persistDraftToStorage(blueprint);
  }, [blueprint]);

  const clearDraft = useCallback(() => {
    setBlueprint(null);
    persistDraftToStorage(null);
  }, []);

  // ------------------------------------------
  // generateBlueprint
  // ------------------------------------------
  const generateBlueprint = useCallback(async (seed: SongSeed) => {
    setIsGenerating(true);
    setError(null);

    try {
      // Determine tone from genre, fall back to first tone
      const tone = getNarrativeToneByGenre(seed.genre) || NARRATIVE_TONES[0];

      // Build and send the LLM prompt, prepending the personal taste profile
      const tasteBlock = getTasteProfileBlockSync();
      const prompt = tasteBlock + buildBlueprintPrompt(seed, tone);
      const response = await generateNarrativeBlueprint(prompt);

      // Look up the selected arc, fall back to first arc
      const arc = getNarrativeArcById(response.selectedArcId) || NARRATIVE_ARCS[0];

      // Assemble the blueprint
      const newBlueprint: NarrativeBlueprint = {
        seedTitle: seed.title,
        seedConcept: seed.concept,
        genre: seed.genre,
        arc,
        tone,
        character: response.character,
      };

      setBlueprint(newBlueprint);
      console.log('[NarrativeEngine] Blueprint generated:', {
        arc: arc.label,
        tone: tone.label,
        stance: response.character.stance,
      });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to generate narrative blueprint';
      console.error('[NarrativeEngine] generateBlueprint error:', errMsg);
      setError(errMsg);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  // ------------------------------------------
  // setArc
  // ------------------------------------------
  const setArc = useCallback((arcId: string) => {
    setBlueprint(prev =>
      prev ? { ...prev, arc: getNarrativeArcById(arcId) || prev.arc } : null
    );
  }, []);

  // ------------------------------------------
  // setToneOverride
  // ------------------------------------------
  const setToneOverride = useCallback((toneId: string | null) => {
    setBlueprint(prev => {
      if (!prev) return null;
      if (toneId) {
        const tone = getNarrativeToneById(toneId);
        return tone ? { ...prev, tone } : prev;
      }
      // null toneId = reset to genre default
      const genreTone = getNarrativeToneByGenre(prev.genre) || NARRATIVE_TONES[0];
      return { ...prev, tone: genreTone };
    });
  }, []);

  // ------------------------------------------
  // regenerateCharacter
  // ------------------------------------------
  const regenerateCharacter = useCallback(async () => {
    if (!blueprint) return;

    setIsGenerating(true);
    setError(null);

    try {
      const prompt = buildRegenerateCharacterPrompt(blueprint);
      const response = await generateNarrativeBlueprint(prompt);

      setBlueprint(prev =>
        prev ? { ...prev, character: response.character } : null
      );

      console.log('[NarrativeEngine] Character regenerated:', {
        stance: response.character.stance,
      });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to regenerate character';
      console.error('[NarrativeEngine] regenerateCharacter error:', errMsg);
      setError(errMsg);
    } finally {
      setIsGenerating(false);
    }
  }, [blueprint]);

  // ------------------------------------------
  // regenerateField
  // ------------------------------------------
  const regenerateField = useCallback(async (field: keyof CharacterBlueprint) => {
    if (!blueprint) return;

    setIsGenerating(true);
    setError(null);

    try {
      const prompt = buildRegenerateFieldPrompt(blueprint, field);
      const result = await generateSingleField(prompt);

      setBlueprint(prev =>
        prev
          ? { ...prev, character: { ...prev.character, [field]: result.trim() } }
          : null
      );

      console.log(`[NarrativeEngine] Field "${field}" regenerated:`, result.trim().slice(0, 80));
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : `Failed to regenerate ${field}`;
      console.error(`[NarrativeEngine] regenerateField(${field}) error:`, errMsg);
      setError(errMsg);
    } finally {
      setIsGenerating(false);
    }
  }, [blueprint]);

  // ------------------------------------------
  // updateCharacterField
  // ------------------------------------------
  const updateCharacterField = useCallback((field: keyof CharacterBlueprint, value: string) => {
    setBlueprint(prev =>
      prev
        ? { ...prev, character: { ...prev.character, [field]: value } }
        : null
    );
  }, []);

  // ------------------------------------------
  // buildBlock (instance method using current blueprint)
  // ------------------------------------------
  const buildBlock = useCallback((): string => {
    if (!blueprint) return '';
    return buildNarrativeBlock(blueprint);
  }, [blueprint]);

  return {
    blueprint,
    isGenerating,
    error,
    generateBlueprint,
    setArc,
    setToneOverride,
    regenerateCharacter,
    regenerateField,
    updateCharacterField,
    buildNarrativeBlock: buildBlock,
    clearDraft,
  };
}
